import {
    Component,
    computed,
    effect,
    inject,
    input,
    numberAttribute,
    resource,
    untracked,
    ChangeDetectionStrategy,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25IconButtonComponent } from "../s25.icon.button.component";
import { EventSummary, EventSummaryService } from "../../modules/s25-swarm-schedule/s25.event.summary.service";
import { Event } from "../../pojo/Event";
import { Item } from "../../pojo/Item";
import { EventProfile, EventService } from "../../services/event.service";
import { UserprefService } from "../../services/userpref.service";
import { ItemService } from "../../util/items/item.service";
import { AngBridgeUtil } from "../../util/ang.bridge.util";
import { EventSync, EventUpdate } from "../../util/event.sync.util";
import { S25Assert } from "../../util/s25.assert";
import { s25Resource } from "../../util/s25.resource";
import { S25EventSummaryDataComponent } from "./s25.event.summary.data.component";
import { EventProfile as PojoEventProfile } from "../../pojo/EventI";

// TODO: better name? put it somewhere else?
const recTypesWithReservationProfile: Array<PojoEventProfile.RecType> = [
    PojoEventProfile.RecType.DateOrTimeList,
    PojoEventProfile.RecType.FreeFormReservationSet,
];

export type EventSummaryData = {
    summary: EventSummary & {
        profiles?: Array<EventProfile>;
    };
    dateFormat: string;
    timeFormat: string;
    eventId?: number;
};

// TODO: consider merging with s25-ng-item-name
@TypeManagerDecorator("s25-ng-event-summary")
@Component({
    selector: "s25-ng-event-summary",
    standalone: true,
    host: {
        "[class.ng-event-summary]": "true",
    },
    template: `
        @if (summary() && dateFormat.value() && timeFormat.value()) {
            <s25-ng-event-summary-data
                [summary]="summary()"
                [profileReservations]="profileReservations()"
                [eventId]="eventId()"
                [dateFormat]="dateFormat.value()"
                [timeFormat]="timeFormat.value()"
            ></s25-ng-event-summary-data>
        }
        <s25-ng-icon-button
            class="refresh btn btn-flat btn-icon"
            [class.disabled]="loading()"
            [rotating]="loading()"
            [disabled]="loading()"
            [type]="'refresh'"
            [ariaLabel]="'Refresh'"
            (clicked)="refresh()"
        />
    `,
    styles: `
        :host {
            display: block;
            background-color: var(--color--surface-bg);
            border-top-left-radius: 3px;
            border-top-right-radius: 3px;
            border: 1px solid var(--color--border);
            padding: 0.625rem 0.3125rem;
            position: relative;

            &:before {
                content: "";
                position: absolute;
                inset: -1px auto -1px -1px;
                background-color: var(--color--objects-event);
                border-top-left-radius: 3px;
                /* border-bottom-left-radius: 3px; */
                border-bottom-left-radius: 0;
                width: 3px;
            }
        }

        .refresh {
            position: absolute;
            right: 11px;
            top: 11px;
        }
    `,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [S25EventSummaryDataComponent, S25IconButtonComponent],
})
export class S25EventSummaryComponent {
    private readonly itemService = inject(ItemService);

    public readonly eventId = input.required({
        transform: numberAttribute,
    });
    public readonly firstProfileOnly = input(true, {
        alias: "type",
        transform: (val: "full" | "not-full") => val === "not-full",
    });

    private readonly $scope = AngBridgeUtil.make$Scope();

    public readonly summaryResource = resource({
        params: () => this.eventId(),
        loader: async ({ params }) => (await EventSummaryService.getSummary(params)).summary,
    });
    public readonly summary = computed<EventSummary>(() =>
        this.summaryResource.hasValue() && !this.summaryResource.isLoading()
            ? this.summaryResource.value()
            : {
                  event_name: "Loading...",
                  event_title: "",
                  loc: [],
                  prof: [],
                  reference: "...",
                  res: [],
                  state: Event.State.Ids.Tentative,
              },
    );
    public readonly dateFormat = s25Resource({ loader: () => UserprefService.getS25Dateformat() });
    public readonly timeFormat = s25Resource({ loader: () => UserprefService.getS25Timeformat() });
    public readonly isLoggedIn = s25Resource({
        loader: () => UserprefService.getLoggedIn(),
    });
    public readonly hasReservationProfiles = computed(() =>
        this.summary()?.prof?.some((prof) => recTypesWithReservationProfile.includes(prof.rec_type)),
    );
    public readonly profilesRq = resource({
        params: () => this.eventId(),
        loader: ({ params }) => EventService.getEventInclude(params, ["reservations"]),
    });
    public readonly profileReservations = computed<Array<EventProfile> | undefined>(() => {
        if (this.profilesRq.hasValue()) {
            return this.profilesRq.value().profile;
        } else {
            return undefined;
        }
    });
    public readonly loading = computed(
        () =>
            this.summaryResource.isLoading() ||
            this.dateFormat.resource.isLoading() ||
            this.timeFormat.resource.isLoading() ||
            this.isLoggedIn.resource.isLoading() ||
            this.profilesRq.isLoading(),
    );

    constructor() {
        AngBridgeUtil.init(this);
        effect(() => {
            if (this.summary() && this.profileReservations() && this.dateFormat.value() && this.timeFormat.value()) {
                this.$scope.$emit("eventDataEmit", {
                    summary: this.hasReservationProfiles()
                        ? {
                              ...this.summary(),
                              profiles: this.profileReservations(),
                          }
                        : this.summary(),
                    dateFormat: this.dateFormat.value()!,
                    timeFormat: this.timeFormat.value()!,
                } satisfies EventSummaryData);
            }
        });
        /*
         * TODO: this is not the best long-term strategy for storing items post-request.
         *  Ideally, it would be done by the service—perhaps as an annotation, e.g.:
         *  ```ts
         *  @CacheItem((result: EventData) => ({
         *      item: { itemId: result.event_id, itemTypeId: Item.Ids.Event, favorite: result.favorite === "T" },
         *      fetched: { all: true },
         *  }))
         *  ```
         */
        effect(() => {
            if (this.profilesRq.hasValue()) {
                const event = this.profilesRq.value();
                untracked(() =>
                    this.itemService.storeItem(
                        {
                            itemId: event.event_id,
                            itemTypeId: Item.Ids.Event,
                            itemName: event.event_name,
                            itemDesc: event.event_title,
                            favorite: event.favorite === "T",
                        },
                        { all: true },
                    ),
                );
            }
        });
        EventSync.addEventListener((event) => {
            if (
                event.detail?.eventId === this.eventId() &&
                this.summaryResource.hasValue() &&
                !this.summaryResource.isLoading()
            ) {
                this.summaryResource.update((summary) => ({
                    ...summary,
                    ...this.getSummaryPatch(event.detail),
                }));
            }
        });
    }

    private getSummaryPatch(data: EventUpdate): Partial<EventSummary> {
        switch (data.property) {
            case "name":
                return { event_name: data.newValue };
            case "title":
                return { event_title: data.newValue };
            case "state":
                return { state: data.newValue };
            default:
                S25Assert.never(data, "Unhandled event update type");
        }
    }

    public refresh(): any {
        if (!this.loading()) {
            this.summaryResource.reload();
            this.isLoggedIn.resource.reload();
            this.profilesRq.reload();
        }
    }
}
